import { Checkbox, InlineError, Radio, Text } from '@box/blueprint-web';
import classNames from 'classnames';
import * as React from 'react';
import type { IntlShape } from 'react-intl';
import { injectIntl } from 'react-intl';
import useId from '../../../../../common/utils/useId';
import { TYPE_RADIO_FIELD } from '../../../../../constants';
import css from './list-field-submitter.module.scss';

type Props = {
  className?: string;
  description?: React.ReactNode;
  error?: React.ReactNode | string;
  fieldType: string;
  intl: IntlShape;
  isDisabled: boolean;
  isInTwoColumn?: boolean;
  isRequired?: boolean;
  label: React.ReactNode;
  onChange: (...args: unknown[]) => unknown;
  options: string[];
  selectedValues: string[];
};

type ListItemsProps = {
  isDisabled: boolean;
  onChange: (...args: unknown[]) => unknown;
  options: string[];
};

const RadioGroup = (
  props: ListItemsProps &
    JSX.LibraryManagedAttributes<
      typeof Radio.Group,
      React.ComponentProps<typeof Radio.Group>
    >,
) => {
  const { onChange, options, isDisabled, ...rest } = props;
  const id = useId('radio-group');

  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  const handleChange = (value: any) => {
    onChange([value]);
  };

  return (
    <Radio.Group {...rest} onValueChange={handleChange} orientation="vertical">
      {options.map(option => {
        return (
          <Radio.Item
            key={`${id}-${option}`}
            disabled={isDisabled}
            label={option}
            value={option}
          />
        );
      })}
    </Radio.Group>
  );
};

const CheckboxGroup = (
  props: ListItemsProps & {
    selectedValues: string[];
  } & JSX.LibraryManagedAttributes<'div', React.ComponentProps<'div'>>,
) => {
  const { onChange, options, selectedValues, isDisabled, ...rest } = props;
  const id = useId('checkbox-group');

  const handleCheckChange = (checked: boolean, value: string) => {
    const changedSelected = checked
      ? [...new Set([...selectedValues, value])]
      : selectedValues.filter(item => item !== value);
    onChange(changedSelected);
  };

  return (
    <div {...rest} role="group">
      {options.map(option => (
        <Checkbox.Item
          key={`${id}-${option}`}
          checked={selectedValues.includes(option)}
          className={css.checkboxItem}
          disabled={isDisabled}
          label={option}
          // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2345
          onCheckedChange={checked => handleCheckChange(checked, option)}
          value={option}
        />
      ))}
    </div>
  );
};

const ListFieldSubmitter = React.memo<Props>((props: Props) => {
  const {
    className,
    isInTwoColumn,
    label,
    description,
    error,
    fieldType,
    onChange,
    selectedValues,
    isRequired,
    options,
    isDisabled,
  } = props;

  const isRadio = TYPE_RADIO_FIELD === fieldType;

  const hasError = !!error;
  const classes = classNames(className, css.listFieldContainer, {
    [css.halfColumnWidth]: isInTwoColumn,
  });

  const labelID = useId('label');
  const errorMessageID = useId('inline-error-');
  const descriptionID = useId('description');

  const ariaDescribedBy = [
    ...(description ? [descriptionID] : []),
    errorMessageID,
  ].join(' ');

  const ariaAttrs = {
    'aria-invalid': hasError,
    'aria-required': isRequired,
    'aria-describedby': ariaDescribedBy,
    'aria-labelledby': labelID,
  } as const;

  return (
    <div className={classes}>
      <Text
        id={labelID}
        as="label"
        // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2820
        color="text-on-light-default"
        variant="subtitle"
      >
        {label}
      </Text>
      <div className={css.listGroup}>
        {isRadio ? (
          <RadioGroup
            {...ariaAttrs}
            isDisabled={isDisabled}
            onChange={onChange}
            options={options}
          />
        ) : (
          <CheckboxGroup
            {...ariaAttrs}
            isDisabled={isDisabled}
            onChange={onChange}
            options={options}
            selectedValues={selectedValues}
          />
        )}
      </div>
      <InlineError
        className={classNames({ [css.error]: isRadio })}
        id={errorMessageID}
      >
        {error}
      </InlineError>
      {!!description && (
        <Text
          as="span"
          className={css.description}
          // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
          color="textOnLight"
          id={descriptionID}
          variant="bodyDefault"
        >
          {description}
        </Text>
      )}
    </div>
  );
});

export { ListFieldSubmitter };
type WrappedProps = Omit<Props, 'intl'>;
const Wrapped = injectIntl(ListFieldSubmitter);
export default Wrapped as React.ComponentType<WrappedProps>;
